/**
 * 予実集計の共通処理。事業部モジュールはここを使って5行契約を満たす。
 *
 * **実績に含めるのは status === 'confirmed' のみ。**(CLAUDE.md の不変条件)
 * approved / submitted は見込み扱いで、実績には入れない。
 */
import { collection, getDocs, query, where, type QueryDocumentSnapshot } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { BOOKED_STATUSES, PENDING_STATUSES, buildSummaryLine, type Account, type Budget, type DepartmentId, type MonthStr, type SummaryLine, type Transaction, type Yen } from '@core/lib/types';

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

/** 事業部×月の確定済み取引(実績の元データ) */
export async function fetchBookedTransactions(
  departmentId: DepartmentId,
  month: MonthStr,
): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), 'transactions'),
      where('department_id', '==', departmentId),
      where('status', 'in', BOOKED_STATUSES),
      where('posting_month', '==', month),
    ),
  );
  return snap.docs.map((d) => withId<Transaction>(d));
}

/** 見込み(承認済・領収書提出済)の取引。実績には含めず、参考表示にのみ使う */
export async function fetchPendingTransactions(
  departmentId: DepartmentId,
  month: MonthStr,
): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), 'transactions'),
      where('department_id', '==', departmentId),
      where('posting_month', '==', month),
      where('status', 'in', PENDING_STATUSES),
    ),
  );
  return snap.docs.map((d) => withId<Transaction>(d));
}

export async function fetchBudgets(
  departmentId: DepartmentId,
  month: MonthStr,
): Promise<Budget[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), 'budgets'),
      where('department_id', '==', departmentId),
      where('month', '==', month),
    ),
  );
  return snap.docs.map((d) => withId<Budget>(d));
}

/**
 * 科目の pl_line に従って取引を5行へ畳む(振り分けの正は accounts.pl_line)。
 * **資金移動(type='transfer')はPLに含めない。** 口座間の付け替えであり損益ではないため、
 * 科目の設定に関わらずここで除外する(decisions.md 2026-08-20)。
 */
export function summarizeTransactions(
  transactions: Transaction[],
  accounts: Account[],
): SummaryLine {
  const lineOf = new Map(accounts.map((a) => [a.id, a.pl_line]));
  let revenue: Yen = 0;
  let cogs: Yen = 0;
  let expense: Yen = 0;

  for (const t of transactions) {
    if (t.type === 'transfer') continue;
    switch (lineOf.get(t.account_id) ?? null) {
      case 'revenue':
        revenue += t.amount;
        break;
      case 'cogs':
        cogs += t.amount;
        break;
      case 'expense':
        expense += t.amount;
        break;
      default:
        break; // 資産・負債(CF管理用)はPLに含めない
    }
  }
  return buildSummaryLine({ revenue, cogs, expense });
}

export function summarizeBudgets(budgets: Budget[], accounts: Account[]): SummaryLine {
  const lineOf = new Map(accounts.map((a) => [a.id, a.pl_line]));
  let revenue: Yen = 0;
  let cogs: Yen = 0;
  let expense: Yen = 0;

  for (const b of budgets) {
    switch (lineOf.get(b.account_id) ?? null) {
      case 'revenue':
        revenue += b.amount;
        break;
      case 'cogs':
        cogs += b.amount;
        break;
      case 'expense':
        expense += b.amount;
        break;
      default:
        break;
    }
  }
  return buildSummaryLine({ revenue, cogs, expense });
}

/** 科目別の内訳(事業部ビューのドリルダウン手前の粒度) */
export type AccountBreakdown = {
  accountId: string;
  accountName: string;
  plLine: Account['pl_line'];
  actual: Yen;
  budget: Yen;
};

export function breakdownByAccount(
  transactions: Transaction[],
  budgets: Budget[],
  accounts: Account[],
): AccountBreakdown[] {
  const map = new Map<string, AccountBreakdown>();
  const ensure = (accountId: string) => {
    let row = map.get(accountId);
    if (!row) {
      const account = accounts.find((a) => a.id === accountId);
      row = {
        accountId,
        accountName: account?.name ?? accountId,
        plLine: account?.pl_line ?? null,
        actual: 0,
        budget: 0,
      };
      map.set(accountId, row);
    }
    return row;
  };

  for (const t of transactions) ensure(t.account_id).actual += t.amount;
  for (const b of budgets) ensure(b.account_id).budget += b.amount;

  const order: Record<string, number> = { revenue: 0, cogs: 1, expense: 2 };
  return [...map.values()].sort(
    (a, b) => (order[a.plLine ?? 'z'] ?? 9) - (order[b.plLine ?? 'z'] ?? 9),
  );
}
